"use client";

import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Projects() {
  const [filter, setFilter] = useState<string>("all");
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const installations = [{
    name: "Burlington Residential Lot",
    type: "rooftop",
    metric: "Cleared 2 acres of dense overgrowth and brush",
    size: "Full Lot Prep",
    year: "Completed 2024",
    image: "https://optimum-pug.10web.cloud/wp-content/uploads/2026/06/Before_06-26.jpg?v=r4ixwb1vig",
    srcset: "https://optimum-pug.10web.cloud/wp-content/uploads/2026/06/Before_06-26.jpg?v=8ap8vcjnnu9",
    sizes: "https://optimum-pug.10web.cloud/wp-content/uploads/2026/06/Before_06-26.jpg?v=lfwxsyxsbur",
    alt: "Forestry mulching on residential property"
  }, {
    name: "Land Clearing in Whitsett, NC",
    type: "ground",
    metric: "\n",
    size: "Trail Clearing",
    year: "Completed 2023",
    image: "https://optimum-pug.10web.cloud/wp-content/uploads/2026/05/newsombefore-after-quadrant.jpg?v=jeiiib95fm",
    srcset: "https://optimum-pug.10web.cloud/wp-content/uploads/2026/05/newsombefore-after-quadrant.jpg?v=ro2g5jfaci",
    sizes: "https://optimum-pug.10web.cloud/wp-content/uploads/2026/05/newsombefore-after-quadrant.jpg?v=zy8rdfumqfb",
    alt: "Path and trail clearing in the woods"
  }, {
    name: "Oak Ridge Farm Clearing",
    type: "rooftop",
    metric: "Removed invasive brush for new pasture",
    size: "Land Clearing",
    year: "Completed 2023",
    image: "https://optimum-pug.10web.cloud/wp-content/uploads/2026/06/IMG_3569.jpg?v=ng6j9kmxf3",
    srcset: "https://optimum-pug.10web.cloud/wp-content/uploads/2026/06/IMG_3569.jpg?v=x2mvxr6wa6i",
    sizes: "https://optimum-pug.10web.cloud/wp-content/uploads/2026/06/IMG_3569.jpg?v=pc2l36u5qwk",
    alt: "Large scale land clearing for farm"
  }];
  const filtered = installations.filter(item => {
    if (filter === "all") return true;
    return item.type === filter;
  });
  return <section data-section-id="18484" id="beforeafterprojects" className="text-foreground py-24 md:py-32 px-6 md:px-10 lg:px-16 relative overflow-hidden bg-accent">
      <div className="absolute top-0 left-0 right-0 -translate-y-px pointer-events-none" aria-hidden="true">
        <svg viewBox="0 0 1440 80" preserveAspectRatio="none" className="w-full h-[48px] md:h-[80px]">
          <path d="M0,80 C360,0 1080,0 1440,80 L1440,0 L0,0 Z" fill="var(--background)" style={{
          fill: "var(--background)"
        }} />
        </svg>
      </div>

      <div className="max-w-[1240px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-end mb-16 md:mb-20">
          <div className="lg:col-span-8">
            <p className="font-default uppercase tracking-[0.14em] text-[0.8125rem] font-medium text-muted-foreground mb-5">
              Our Recent Work
            </p>
            <h2 className="font-default font-semibold text-[clamp(2rem,4.2vw,3.5rem)] leading-[1.08] tracking-[-0.018em] text-secondary">The CHIPPER Transformation</h2>
            <p className="font-default text-sm md:text-base text-muted-foreground mt-4 max-w-2xl">
              See how our high-efficiency forestry mulching turns dense, unusable brush into clean, nutrient-rich ground cover in a single pass.
            </p>
          </div>

          <div className="lg:col-span-4 lg:flex lg:justify-end">
            <ToggleGroup type="single" value={filter} onValueChange={value => {
            if (value) setFilter(value);
          }} className="bg-transparent gap-2 flex-wrap">
              <ToggleGroupItem value="all" aria-label="Show all projects" className="rounded-lg border border-primary/40 bg-transparent text-foreground px-5 py-2.5 text-sm font-medium hover:bg-primary/10 data-[state=on]:bg-primary data-[state=on]:text-white data-[state=on]:border-primary transition-colors duration-200">
                All
              </ToggleGroupItem>
              <ToggleGroupItem value="rooftop" aria-label="Show residential projects" className="rounded-lg border border-primary/40 bg-transparent text-foreground px-5 py-2.5 text-sm font-medium hover:bg-primary/10 data-[state=on]:bg-primary data-[state=on]:text-white data-[state=on]:border-primary transition-colors duration-200">
                Residential
              </ToggleGroupItem>
              <ToggleGroupItem value="ground" aria-label="Show commercial projects" className="rounded-lg border border-primary/40 bg-transparent text-foreground px-5 py-2.5 text-sm font-medium hover:bg-primary/10 data-[state=on]:bg-primary data-[state=on]:text-white data-[state=on]:border-primary transition-colors duration-200">
                Commercial
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
        </div>

        <div ref={ref} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12 md:gap-y-14">
          {filtered.map((item, i) => <article key={item.name} data-index={i} className={`group transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
          transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
          transitionDelay: `${i * 80}ms`
        }}>
              <div className="overflow-hidden rounded-lg aspect-[4/5] bg-muted shadow-md">
                <img src={item.image} data-wvc-srcset={item.srcset} data-wvc-sizes={item.sizes} alt={item.alt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-[600ms] group-hover:scale-[1.03]" style={{
              transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)"
            }} />
              </div>

              <div className="pt-5">
                <h3 className="font-default text-[clamp(1.0625rem,1.3vw,1.25rem)] font-semibold tracking-[-0.005em] leading-[1.3] text-foreground inline-block relative">
                  <span className="relative">
                    {item.name}
                    <span className="absolute left-0 -bottom-0.5 h-px w-0 bg-primary transition-[width] duration-[240ms] ease-out group-hover:w-full" aria-hidden="true" />
                  </span>
                </h3>
                <p className="font-default text-sm text-muted-foreground mt-2 leading-[1.45]">
                  {item.metric}
                </p>
                
              </div>
            </article>)}
        </div>
      </div>
    </section>;
}